import { z } from 'zod';
import type { MatchDiagnosticBundle } from '../diagnostics/analyzer';
import { normalizeDiagnosticBundle } from '../diagnostics/compact';
import { DECK_SIZE, isValidDeckComposition } from '../engine/deck-rules';
import type { ReplayFile } from './replay';

const playerIdSchema = z.enum(['player', 'bot']);
const pointSchema = z.object({ x: z.number().finite(), y: z.number().finite() });
const resultSchema = z.object({
  winner: z.union([playerIdSchema, z.literal('draw')]),
  reason: z.enum(['king-destroyed', 'time', 'sudden-death', 'tiebreak']),
  crowns: z.object({ player: z.number().int().nonnegative(), bot: z.number().int().nonnegative() }),
  tick: z.number().int().nonnegative(),
});
const rulesSchema = z.object({
  id: z.string().min(1), tickRate: z.number().int().positive(), countdownTicks: z.number().int().nonnegative(),
  normalTicks: z.number().int().nonnegative(), overtimeTicks: z.number().int().nonnegative(), startingElixirMilli: z.number().int().nonnegative(),
  maxElixirMilli: z.number().int().positive(),
  singleElixirPerTickMilli: z.number().int().nonnegative(), doubleElixirPerTickMilli: z.number().int().nonnegative(), tripleElixirPerTickMilli: z.number().int().nonnegative(),
  normalDoubleElixirStartTick: z.number().int().nonnegative(), overtimeTripleElixirStartTick: z.number().int().nonnegative(),
  singleCardCooldownTicks: z.number().int().nonnegative(), doubleCardCooldownTicks: z.number().int().nonnegative(), tripleCardCooldownTicks: z.number().int().nonnegative(),
});
const commandSchema = z.object({
  type: z.literal('play-card'), tick: z.number().int().nonnegative(), sequence: z.number().int().positive(),
  playerId: playerIdSchema, cardId: z.string().min(1), position: pointSchema,
});
const deckSchema = z.array(z.string().min(1))
  .length(DECK_SIZE)
  .refine(isValidDeckComposition, `Deck must contain exactly ${DECK_SIZE} unique cards`);

const styleSchema = z.object({
  aggression: z.number().int().min(0).max(1_000), cycle: z.number().int().min(0).max(1_000), beatdown: z.number().int().min(0).max(1_000),
  airPressure: z.number().int().min(0).max(1_000), swarm: z.number().int().min(0).max(1_000), spellControl: z.number().int().min(0).max(1_000),
  defense: z.number().int().min(0).max(1_000), lanePreference: z.number().int().min(-1_000).max(1_000),
});
const botStrategySchema = z.object({
  schemaVersion: z.literal(1), botId: z.string().min(1), botVersion: z.string().min(1), deckPolicyId: z.string().min(1), deckPolicyVersion: z.string().min(1),
  modelVersion: z.string().min(1).nullable(), modelHash: z.string().min(1), confidence: z.number().int().min(0).max(1_000), sourceMatches: z.number().int().nonnegative(),
  style: styleSchema, selectedDeck: deckSchema, deckScore: z.number().finite(), rationale: z.array(z.string().min(1)),
});
const matchMetadataSchema = z.object({
  mode: z.enum(['bot', 'network']),
  resultTrust: z.enum(['local', 'peer-confirmed', 'server-verified']),
  networkMatchId: z.string().min(1).nullable(),
  localSide: playerIdSchema,
}).refine((match) => match.mode === 'network' ? match.networkMatchId !== null : match.networkMatchId === null, 'Network match metadata must include a network match id');
const replayBaseSchema = z.object({
  engineVersion: z.string().min(1), contentVersion: z.string().min(1), seed: z.number().int().nonnegative(),
  rulesetId: z.string().min(1), rules: rulesSchema, arenaId: z.string().min(1),
  decks: z.object({ player: deckSchema, bot: deckSchema }),
  commands: z.array(commandSchema), checkpoints: z.array(z.object({ tick: z.number().int().nonnegative(), checksum: z.string().min(1) })),
  result: resultSchema.nullable(),
});
const currentReplaySchema = replayBaseSchema.extend({ formatVersion: z.literal(3), botStrategy: botStrategySchema.optional(), match: matchMetadataSchema });
const previousReplaySchema = replayBaseSchema.extend({ formatVersion: z.union([z.literal(1), z.literal(2)]), botStrategy: botStrategySchema.optional() })
  .transform((replay) => ({ ...replay, formatVersion: 3 as const, match: { mode: 'bot' as const, resultTrust: 'local' as const, networkMatchId: null, localSide: 'player' as const } }));
export const replayFileSchema = z.union([currentReplaySchema, previousReplaySchema]);

const manifestBaseSchema = z.object({ matchId: z.string().min(1), createdAt: z.string().min(1), engineVersion: z.string().min(1), contentVersion: z.string().min(1) });
const legacyManifestSchema = manifestBaseSchema.extend({ formatVersion: z.literal(1) }).passthrough();
const currentManifestSchema = manifestBaseSchema.extend({
  formatVersion: z.literal(2),
  localizationVersion: z.string().min(1),
  matchMode: z.enum(['bot', 'network']),
  resultTrust: z.enum(['local', 'peer-confirmed', 'server-verified']),
  networkMatchId: z.string().min(1).nullable(),
  buildId: z.string().min(1).optional(),
  termination: z.object({ reason: z.string().min(1), tick: z.number().int().nonnegative(), abnormal: z.boolean() }).optional(),
}).passthrough().refine((manifest) => manifest.matchMode === 'network' ? manifest.networkMatchId !== null : manifest.networkMatchId === null, 'Network diagnostic metadata must include a network match id');
const diagnosticBundleSchema = z.object({
  manifest: z.union([currentManifestSchema, legacyManifestSchema]),
  replay: replayFileSchema,
  records: z.array(z.object({ schemaVersion: z.literal(1), matchId: z.string().min(1), eventId: z.number().int().positive(), tick: z.number().int().nonnegative(), phase: z.string(), source: z.string(), level: z.string(), code: z.string(), payload: z.unknown() }).passthrough()),
  checkpoints: z.array(z.object({ tick: z.number().int().nonnegative(), checksum: z.string().min(1) })),
  metrics: z.object({ records: z.number().int().nonnegative() }).passthrough(),
  findings: z.array(z.object({ ruleId: z.string(), severity: z.string(), tickFrom: z.number(), tickTo: z.number() }).passthrough()),
  summary: z.object({ result: resultSchema.nullable(), critical: z.number().int().nonnegative(), errors: z.number().int().nonnegative(), warnings: z.number().int().nonnegative() }),
  debug: z.unknown().optional(),
});

export function parseReplayFile(value: unknown): ReplayFile {
  return replayFileSchema.parse(value) as unknown as ReplayFile;
}

export function parseDiagnosticBundle(value: unknown): MatchDiagnosticBundle {
  const parsed = diagnosticBundleSchema.parse(normalizeDiagnosticBundle(value));
  if (parsed.manifest.formatVersion === 2) {
    return {
      ...parsed,
      manifest: {
        ...parsed.manifest,
        buildId: parsed.manifest.buildId ?? 'legacy',
        termination: parsed.manifest.termination ?? { reason: parsed.summary.result ? 'completed' : 'abandoned', tick: parsed.summary.result?.tick ?? parsed.checkpoints.at(-1)?.tick ?? 0, abnormal: parsed.summary.result === null },
      },
      debug: parsed.debug ?? null,
    } as unknown as MatchDiagnosticBundle;
  }
  return {
    ...parsed,
    manifest: {
      ...parsed.manifest,
      formatVersion: 2,
      localizationVersion: 'legacy',
      matchMode: parsed.replay.match.mode,
      resultTrust: parsed.replay.match.resultTrust,
      networkMatchId: parsed.replay.match.networkMatchId,
      buildId: 'legacy',
      termination: { reason: parsed.summary.result ? 'completed' : 'abandoned', tick: parsed.summary.result?.tick ?? parsed.checkpoints.at(-1)?.tick ?? 0, abnormal: parsed.summary.result === null },
    },
    debug: null,
  } as unknown as MatchDiagnosticBundle;
}
